import { FC, ReactNode, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { STAFF_ROUTE } from "../../../app/router/routes";
import { billingService } from "../../../entities/billing";
import { useGlobalMessageActions } from "../../../entities/globalMessage";
import { useMyActions } from "../../../entities/my";
import { getDateUTC } from "../../../shared/lib/helpers/getDateUTC";
import { AuthError } from "../../../shared/lib/error/AuthError";
import { MyError } from "../../../shared/lib/error/MyError";
import { Calendar } from "../../../shared/ui/calendar";
import { MyInput } from "../../../shared/ui/input";
import { Table } from "../../../shared/ui/table";
import { OpenContainer } from "../../../features/openContainer";
import lupaImg from "../../../features/searchItems/lib/assets/lupa.png";
import classes from "../../tutorSalaries/ui/tutorSalaries.module.scss";

interface IWorkerPayrollRow {
    user_id: string;
    full_name: string;
    email: string;
    roles: string[];
    salary: string;
    balance: string;
}

const getCell = (text: ReactNode) => text || <span>-</span>

export const WorkerPayroll: FC = () => {
    const [items, setItems] = useState<IWorkerPayrollRow[]>([])
    const [searchValue, setSearchValue] = useState('')
    const [isLoading, setIsLoading] = useState(false)
    const [hasLoaded, setHasLoaded] = useState(false)
    const [loadError, setLoadError] = useState('')
    const requestRef = useRef<AbortController | null>(null)
    const {setGlobalMessage} = useGlobalMessageActions()
    const {setIsAuth} = useMyActions()

    useEffect(() => () => requestRef.current?.abort(), [])

    const loadWorkers = useCallback(async (startDate: Date, endDate: Date) => {
        requestRef.current?.abort()
        const controller = new AbortController()
        requestRef.current = controller
        try{
            setIsLoading(true)
            setLoadError('')
            setHasLoaded(false)
            setItems([])
            const workers = await billingService.listWorkerAccruals(
                getDateUTC(startDate), getDateUTC(endDate), controller.signal,
            )
            if(controller.signal.aborted) return
            setItems(workers.map(worker => ({
                user_id: worker.userId,
                full_name: worker.userName,
                email: worker.email,
                roles: worker.roleDetails?.length
                    ? worker.roleDetails.map(role => role.name || role.code)
                    : worker.roles ?? [],
                salary: worker.summary.unpaid,
                balance: worker.balance,
            })))
            setHasLoaded(true)
        }
        catch(e){
            if(controller.signal.aborted) return
            setItems([])
            setLoadError('Не удалось загрузить зарплаты работников. Выберите период повторно.')
            console.log(e)
            if(e instanceof AuthError){
                setIsAuth(false)
                setGlobalMessage({message: e.message, type: 'error'})
            }
            else if(e instanceof MyError){
                setGlobalMessage({message: e.message, type: 'error'})
            }
            else{
                setGlobalMessage({message: 'Ошибка при получении зарплат работников', type: 'error'})
            }
        }
        finally{
            if(!controller.signal.aborted) setIsLoading(false)
        }
    }, [setGlobalMessage, setIsAuth, setIsLoading])

    const filteredItems = useMemo(() => {
        const query = searchValue.trim().toLocaleLowerCase('ru-RU')
        if(!query){
            return items
        }

        return items.filter(item => [
            item.full_name,
            item.email,
            item.roles.join(' '),
            String(item.user_id),
        ].join(' ').toLocaleLowerCase('ru-RU').includes(query))
    }, [items, searchValue])

    const total = useMemo(() => filteredItems
        .reduce((sum, item) => sum + Number(item.salary || 0), 0)
        .toLocaleString('ru-RU', {maximumFractionDigits: 2}), [filteredItems])

    const rows = useMemo(() => filteredItems.map(item => [
        getCell(<Link className={classes.nameLink} to={`${STAFF_ROUTE.path}/${item.user_id}`}>{item.full_name || '-'}</Link>),
        getCell(item.email),
        getCell(item.roles.join(', ') || 'Без ролей'),
        getCell(item.salary),
        getCell(item.balance),
    ]), [filteredItems])

    return (
        <OpenContainer
            title="Зарплатный работников"
            allowOverflow
            icon={<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M4 7H20V17H4z" /><path d="M8 11H16M8 14H13" /></svg>}
        >
            <section className={classes.container}>
                <section className={classes.controls}>
                    <Calendar isLoading={isLoading} onDateRangeSelect={(startDate, endDate) => {
                        if(startDate && endDate) loadWorkers(startDate, endDate)
                        else {
                            requestRef.current?.abort()
                            setIsLoading(false)
                            setItems([])
                            setHasLoaded(false)
                            setLoadError('')
                        }
                    }} />
                </section>
                {isLoading && <p role="status">Загрузка зарплат работников…</p>}
                {loadError && <p role="alert">{loadError}</p>}
                {hasLoaded && items.length === 0 && <p role="status">Нет доступных работников для отображения.</p>}
                {items.length > 0 && (
                    <>
                        <section className={classes.total}>
                            Общая сумма зарплаты: {total} ₽
                        </section>
                        <section className={classes.search}>
                            <MyInput
                                value={searchValue}
                                setValue={setSearchValue}
                                placeholder="Поиск по ФИО, email или роли"
                                iconSrc={lupaImg}
                            />
                        </section>
                        <section className={classes.wrapTable}>
                            <Table
                                titles={['ФИО', 'Email', 'Роли', 'К выплате за период', 'Баланс']}
                                rows={rows}
                            />
                        </section>
                    </>
                )}
            </section>
        </OpenContainer>
    )
}
